// src/components/FacilitySection.jsx
import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './FacilitySection.css';

const facilities_ja = [
  {
    title: '和室', subtitle: 'Japanese-style Room',
    description: '1階には庭を望む広間、2階には静かな寝室。最大8名様までお休みいただける寝具を完備しています。茶室やサービスルームもあり、おひとりの時間も大切にできます。',
    images: ['/images/room.jpg', '/images/machiya1.jpg', '/images/room2.jpg', '/images/room4.jpg', '/images/room5.jpg', '/images/room6.jpg'],
  },
  {
    title: 'ダイニング & キッチン', subtitle: 'Dining & Kitchen',
    description: '木のぬくもりを感じるカウンターと、8名分の食器・IHコンロ・調理器具を備えた最新キッチン。',
    images: ['/images/renovation2.jpg', '/images/kicthen.jpg', '/images/kicthen2.jpg', '/images/kicthen3.jpg', '/images/kicthen4.jpg', '/images/kicthen5.jpg', '/images/kicthen6.jpg', '/images/crafts2.jpg'],
    popup: {
      label: 'アメニティ・キッチン用品一覧',
      content: (
        <div className="amenities-popup">
          <h4>アメニティ一覧</h4>
          <ul><li>ドライヤー</li><li>バスタオル／フェイスタオル</li><li>シャンプー／コンディショナー</li><li>ボディソープ</li><li>ハンドソープ</li><li>衣類スチーマー</li><li>洗濯洗剤</li><li>ウォッシュタオル</li><li>歯ブラシ／歯磨き粉</li><li>Wi-Fi</li><li>エアコン</li><li>浴衣</li></ul>
          <h4>キッチン用品一覧</h4>
          <ul><li>IHコンロ（2口）</li><li>冷蔵庫（冷凍室あり）</li><li>電子レンジ</li><li>トースター</li><li>電気ケトル</li><li>食器乾燥機</li><li>炊飯器</li><li>調理器具一式（鍋、フライパン等）</li><li>食器類8名分</li><li>ふきん</li><li>キッチン洗剤／スポンジ</li><li>コーヒー・オリジナル棒茶</li></ul>
        </div>
      )
    }
  },
  {
    title: '水回り', subtitle: 'Bath & Washroom',
    description: '中庭を眺めながらの開放的な浴室、広い洗面所、洗濯機、2か所の温水洗浄便座付きトイレなど、長期滞在にも対応。',
    images: ['/images/renovation1.jpg', '/images/bath2.jpg', '/images/bath3.jpg', '/images/bath4.jpg', '/images/bath1.jpg'],
  },
  {
    title: '庭園', subtitle: 'Gardens',
    description: '暖簾が目印の表門から、表庭、前庭、中庭の3か所の庭園をお楽しみいただけます。四季折々の庭園をお楽しみください。',
    images: ['/images/hero1.jpg', '/images/hero2.jpg', '/images/garden1.png', '/images/garden2.jpg', '/images/feature-garden.jpg'],
  },
  {
    title: '駐車場・喫煙スペース', subtitle: 'Parking & Smoking',
    description: '無料駐車場を3台分ご用意。施設脇に喫煙スペースもございます。',
    images: ['/images/parking.jpg', '/images/smoking.jpg'],
  },
];

const facilities_en = [
  {
    title: 'Japanese-style Room', subtitle: '',
    description: 'On the first floor, a spacious living room overlooks the garden. On the second floor, a quiet bedroom ensures restful sleep for up to 8 guests. There is also a tea room and a service room for private moments.',
    images: facilities_ja[0].images,
  },
  {
    title: 'Dining & Kitchen', subtitle: '',
    description: 'Enjoy a warm wooden counter and a modern kitchen equipped with tableware for 8 guests, IH stove, and cooking utensils.',
    images: facilities_ja[1].images,
    popup: {
      label: 'Amenities & Kitchen Items',
      content: (
        <div className="amenities-popup">
          <h4>Amenities</h4>
          <ul><li>Hair dryer</li><li>Bath towels / Face towels</li><li>Shampoo / Conditioner</li><li>Body soap</li><li>Hand soap</li><li>Clothes steamer</li><li>Laundry detergent</li><li>Wash towel</li><li>Toothbrush / Toothpaste</li><li>Wi-Fi</li><li>Air conditioning</li><li>Yukata</li></ul>
          <h4>Kitchen Items</h4>
          <ul><li>IH stove (2 burners)</li><li>Refrigerator (with freezer)</li><li>Microwave</li><li>Toaster</li><li>Electric kettle</li><li>Dish dryer</li><li>Rice cooker</li><li>Cooking utensils</li><li>Tableware for 8 guests</li><li>Dish towels</li><li>Dish soap / Sponge</li><li>Coffee & Original Roasted Tea</li></ul>
        </div>
      )
    }
  },
  {
    title: 'Bath & Washroom', subtitle: '',
    description: 'A relaxing bath with a view of the courtyard, a spacious washroom, washing machine, and two toilets with bidets – perfect for long stays.',
    images: facilities_ja[2].images,
  },
  {
    title: 'Gardens', subtitle: '',
    description: 'From the front gate with its traditional noren curtain, enjoy three types of gardens: front, approach, and inner. Experience the beauty of the seasons.',
    images: facilities_ja[3].images,
  },
  {
    title: 'Parking & Smoking Area', subtitle: '',
    description: 'Free parking available for 3 cars. A designated smoking area is also provided beside the building.',
    images: facilities_ja[4].images,
  },
];

const FacilitySection = ({ isEnglish }) => {
  const [modalContent, setModalContent] = useState(null);
  const [isLargeModal, setIsLargeModal] = useState(false);
  const [lightboxData, setLightboxData] = useState({ isOpen: false, images: [], initialIndex: 0 });

  const facilities = isEnglish ? facilities_en : facilities_ja;

  const fadeUpProps = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const openLightbox = (images, index) => {
    setLightboxData({ isOpen: true, images, initialIndex: index });
  };

  const closeLightbox = () => {
    setLightboxData({ ...lightboxData, isOpen: false });
  };

  return (
    <section className="facility-section">
      <h2 className="facility-title">{isEnglish ? 'Facility' : '施設紹介'}</h2>

      {facilities.map((facility, index) => {
        // 画像が少ない場合のエラー回避
        const hasThumbnails = facility.images.length > 1;
        const mainImage = facility.images[0];
        const thumbImages = facility.images.slice(1, 5); // 最大4枚までサムネイル表示
        const remainingCount = facility.images.length - 5;

        return (
          <motion.div
            className={`facility-block ${index % 2 === 1 ? 'reverse' : ''}`}
            key={index}
            {...fadeUpProps}
          >
            <div className="facility-text">
              <h3>
                <span className="en-title">{facility.title}</span>
                {facility.subtitle && (
                  <>
                    <br />
                    <span className="jp-title">{facility.subtitle}</span>
                  </>
                )}
              </h3>
              <p>{facility.description}</p>
              {facility.popup && (
                <button
                  className="popup-link"
                  onClick={() => setModalContent(facility.popup.content)}
                >
                  {facility.popup.label}
                </button>
              )}
            </div>

            {/* 新しいギャラリーパネルレイアウト */}
            <div className={`facility-gallery ${!hasThumbnails ? 'single-image' : ''}`}>
              <div
                className="gallery-main-img"
                onClick={() => openLightbox(facility.images, 0)}
              >
                <img src={mainImage} alt={`${facility.title} main`} />
              </div>

              {hasThumbnails && (
                <div className="gallery-thumbs">
                  {thumbImages.map((img, thumbIdx) => {
                    const globalIdx = thumbIdx + 1;
                    const isLastThumb = thumbIdx === 3;
                    const showOverlay = isLastThumb && remainingCount > 0;

                    return (
                      <div
                        key={thumbIdx}
                        className="gallery-thumb-item"
                        onClick={() => openLightbox(facility.images, globalIdx)}
                      >
                        <img src={img} alt={`${facility.title} ${globalIdx}`} />
                        {showOverlay && (
                          <div className="gallery-more-overlay">
                            <span>+{remainingCount}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        );
      })}

      {/* アメニティ等のテキストモーダル */}
      <AnimatePresence>
        {modalContent && (
          <motion.div
            className="modal-overlay"
            onClick={() => setModalContent(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={`modal-content ${isLargeModal ? 'large-modal' : ''}`}
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
            >
              <button className="modal-close" onClick={() => setModalContent(null)}>×</button>
              {modalContent}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 画像拡大用 Lightbox */}
      <AnimatePresence>
        {lightboxData.isOpen && (
          <motion.div
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox} /* ★追加：背景クリックで閉じる */
          >
            <button className="lightbox-close" onClick={closeLightbox}>×</button>

            {/* ★追加：スライダー部分のクリックで背景まで閉じないようにストッパーを入れる */}
            <div
              className="lightbox-slider-wrapper"
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%', maxWidth: '1000px' }}
            >
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={30}
                slidesPerView={1}
                initialSlide={lightboxData.initialIndex}
                navigation
                pagination={{ clickable: true }}
                className="lightbox-swiper"
              >
                {lightboxData.images.map((img, idx) => (
                  <SwiperSlide key={idx} className="lightbox-slide">
                    <img src={img} alt={`Gallery enlarged ${idx}`} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default FacilitySection;